import { useState, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import { Hotel, Plus, Minus, Info } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useAppContext } from "@/lib/onboardingContext";
import { resolveHotelDepartments, type ResolvedHotelRole } from "@/lib/hotelStaffData";

type Counts = Record<string, number>;

function priorityVariant(priority: ResolvedHotelRole["priority"]) {
  switch (priority) {
    case "essential":
      return "default" as const;
    case "recommended":
      return "secondary" as const;
    case "optional":
    default:
      return "outline" as const;
  }
}

/**
 * Outlet-driven role bumps. Given the outlets configured during onboarding,
 * we compute recommended *additional* counts on top of the baseline so each
 * outlet contributes the roles it actually needs. Keys are
 * `${dept.id}::${role.name}` to match the counts state shape.
 */
function bumpsFromOutlets(
  outlets: Array<{ name: string; type: string; seats?: number }>,
): Counts {
  const bumps: Counts = {};
  const add = (key: string, n: number) => {
    if (n > 0) bumps[key] = (bumps[key] || 0) + n;
  };

  // (housekeeping bumps are computed separately from room data — see
  // `bumpsFromRooms` below — so this function only handles outlet roles)

  let barOutletCount = 0;
  let spaOutletCount = 0;

  for (const o of outlets) {
    const type = (o.type || "").toLowerCase();
    const seats = Number(o.seats) || 0;

    if (type === "restaurant") {
      add("fb::Restaurant Manager — per outlet", 1);
      add("culinary::Chef de Cuisine — per outlet", 1);
      add("fb::Maître d'", 1);
      add("fb::Assistant Restaurant Manager", seats >= 60 ? 1 : 0);
      // Kitchen brigade scales with seats.
      add("culinary::Chef de Partie", Math.max(1, Math.ceil(seats / 30)));
      add("culinary::Commis Chef", Math.max(1, Math.ceil(seats / 25)));
      add("fb::Banqueting Coordinator", seats >= 80 ? 1 : 0);
    } else if (type === "bar" || type === "pool bar" || type === "lobby lounge") {
      barOutletCount += 1;
      // First bar outlet seeds a Head Bartender; every bar gets bartenders sized to seats.
      if (barOutletCount === 1) add("bar::Head Bartender", 1);
      const bartenders = Math.max(1, Math.ceil(seats / 15));
      add("bar::Bartender", bartenders);
      add("bar::Barback", seats >= 30 ? 1 : 0);
    } else if (type === "café" || type === "cafe") {
      // Café = small F&B outlet — light service team + 1 cook.
      add("fb::Restaurant Manager — per outlet", 1);
      add("culinary::Commis Chef", 1);
      add("bar::Bartender", Math.max(1, Math.ceil(seats / 20))); // counter staff stand-in
    } else if (type === "room service") {
      add("fb::In-Room Dining Manager", 1);
      add("fb::In-Room Dining Supervisor", 1);
    } else if (type === "banqueting") {
      add("fb::Banqueting & Events Manager", 1);
      add("fb::Banqueting Coordinator", 1);
      add("culinary::Commis Chef", Math.max(1, Math.ceil(seats / 40)));
    } else if (type === "spa") {
      spaOutletCount += 1;
      if (spaOutletCount === 1) add("spa::Spa Director", 1);
      add("spa::Spa Manager", 1);
      add("spa::Spa Receptionist", 1);
      add("spa::Therapist / Beautician", Math.max(2, Math.ceil(seats / 4)));
    }
  }
  return bumps;
}

/**
 * Housekeeping bumps from configured guest rooms. We size the cleaning team
 * around **occupied room-nights/day** (rooms × occupancy %), not total room
 * count, since unoccupied rooms don't generate cleaning work.
 *
 * Industry rules of thumb (luxury / 5-star, 2025):
 *   • Room Attendant            — 1 per ~14 occupied rooms / shift
 *   • Floor Supervisor          — 1 per ~60 occupied rooms (inspects ~4 RAs)
 *   • Turndown Attendant        — 1 per ~30 occupied rooms (PM shift)
 *   • Public Area Attendant     — 1 per ~60 total rooms (lobby/corridors scale with size)
 *   • Laundry Attendant         — 1 per ~80 occupied rooms
 *   • Assistant Exec Housekeeper — +1 once property exceeds 80 total rooms
 *   • Public Area Supervisor    — +1 once property exceeds 100 total rooms
 *   • Laundry Manager           — +1 once property exceeds 120 total rooms
 */
function bumpsFromRooms(
  rooms: Array<{ type: string; quantity: number; adr: number; occupancy: number }>,
): Counts {
  const bumps: Counts = {};
  const add = (key: string, n: number) => {
    if (n > 0) bumps[key] = (bumps[key] || 0) + n;
  };

  let totalRooms = 0;
  let occupiedRoomNights = 0;
  for (const r of rooms) {
    const qty = Number(r.quantity) || 0;
    const occ = Math.max(0, Math.min(100, Number(r.occupancy) || 0));
    totalRooms += qty;
    occupiedRoomNights += qty * (occ / 100);
  }

  if (totalRooms === 0) return bumps;

  // Core attendant team scales with occupied rooms.
  add("housekeeping::Room Attendant", Math.max(1, Math.ceil(occupiedRoomNights / 14)));
  add("housekeeping::Floor Supervisor / Inspector", Math.max(1, Math.ceil(occupiedRoomNights / 60)));
  add("housekeeping::Turndown Attendant", Math.max(0, Math.ceil(occupiedRoomNights / 30)));
  add("housekeeping::Laundry Attendant", Math.max(0, Math.ceil(occupiedRoomNights / 80)));

  // Public areas scale with property footprint, not occupancy.
  add("housekeeping::Public Area Attendant", Math.max(1, Math.ceil(totalRooms / 60)));

  // Larger properties unlock additional management layers.
  if (totalRooms > 80) add("housekeeping::Assistant Executive Housekeeper", 1);
  if (totalRooms > 100) add("housekeeping::Public Area Supervisor", 1);
  if (totalRooms > 120) add("housekeeping::Laundry Manager", 1);

  return bumps;
}

/**
 * Specialized staffing planner for the Hotel & Lodging business type.
 *
 * Renders a per-department accordion (collapsed by default), with +/-
 * quantity selectors per role and a live monthly cost total at the bottom.
 * Salary ranges come from the same luxury 5-star city dataset that powers
 * the F&B planner, derived via `resolveHotelDepartments(city)`.
 */
export default function HotelStaffingControl() {
  const { onboarding } = useAppContext();
  const city = onboarding?.city || "miami";
  const outlets = (onboarding?.outlets ?? []) as Array<{
    name: string; type: string; seats?: number;
  }>;
  const guestRooms = (onboarding?.guestRooms ?? []) as Array<{
    type: string; quantity: number; adr: number; occupancy: number;
  }>;

  const { departments, profile } = useMemo(
    () => resolveHotelDepartments(city),
    [city],
  );
  const symbol = profile.currencySymbol;

  // Derived room metrics for the seeding banner.
  const { totalRooms, occupiedRoomNights } = useMemo(() => {
    let total = 0;
    let occ = 0;
    for (const r of guestRooms) {
      const qty = Number(r.quantity) || 0;
      const o = Math.max(0, Math.min(100, Number(r.occupancy) || 0));
      total += qty;
      occ += qty * (o / 100);
    }
    return { totalRooms: total, occupiedRoomNights: occ };
  }, [guestRooms]);

  // Initialize counts: essentials default to 1, others to 0, then apply
  // outlet- and room-driven bumps so the configured property seeds appropriate roles.
  // Re-init when the city, outlets, or rooms change.
  const initialCounts = useMemo<Counts>(() => {
    const next: Counts = {};
    const validKeys = new Set<string>();
    for (const dept of departments) {
      for (const role of dept.roles) {
        const key = `${dept.id}::${role.name}`;
        next[key] = role.priority === "essential" ? 1 : 0;
        validKeys.add(key);
      }
    }
    // Apply outlet bumps (additive on top of essentials).
    const outletBumps = bumpsFromOutlets(outlets);
    for (const [key, delta] of Object.entries(outletBumps)) {
      if (validKeys.has(key)) {
        next[key] = (next[key] || 0) + delta;
      }
    }
    // Apply room-driven housekeeping bumps. These *replace* the baseline
    // (rather than stacking on the essential default of 1) since the math
    // already includes minimum coverage via Math.max(1, …).
    const roomBumps = bumpsFromRooms(guestRooms);
    for (const [key, value] of Object.entries(roomBumps)) {
      if (validKeys.has(key)) {
        next[key] = value;
      }
    }
    return next;
  }, [departments, outlets, guestRooms]);

  const [counts, setCounts] = useState<Counts>(initialCounts);

  // Reset counts whenever the resolved roster or outlets change.
  useMemo(() => setCounts(initialCounts), [initialCounts]);


  const update = useCallback((key: string, delta: number) => {
    setCounts(prev => ({ ...prev, [key]: Math.max(0, (prev[key] || 0) + delta) }));
  }, []);

  // Per-department subtotals + total.
  const { departmentTotals, departmentStaff, totalMonthly, totalStaff } = useMemo(() => {
    let total = 0;
    let staff = 0;
    const totals: Record<string, number> = {};
    const staffPerDept: Record<string, number> = {};
    for (const dept of departments) {
      let deptTotal = 0;
      let deptStaff = 0;
      for (const role of dept.roles) {
        const c = counts[`${dept.id}::${role.name}`] || 0;
        deptTotal += c * role.monthlyAvg;
        deptStaff += c;
      }
      totals[dept.id] = deptTotal;
      staffPerDept[dept.id] = deptStaff;
      total += deptTotal;
      staff += deptStaff;
    }
    return { departmentTotals: totals, departmentStaff: staffPerDept, totalMonthly: total, totalStaff: staff };
  }, [departments, counts]);

  const fmtMoney = (n: number) => `${symbol}${Math.round(n).toLocaleString()}`;

  return (
    <TooltipProvider delayDuration={200}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.26 }}
        className="bg-card rounded-xl p-6 border border-border shadow-card mb-8"
      >
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Hotel className="w-5 h-5 text-primary" />
            <h3 className="font-heading font-medium text-foreground">
              Hotel Staffing Planner
              <span className="ml-2 text-xs font-normal text-muted-foreground">
                · {profile.label} rates
              </span>
            </h3>
          </div>
          <div className="text-right">
            <div className="text-xs text-muted-foreground">{totalStaff} staff</div>
            <div className="text-sm font-medium text-primary">{fmtMoney(totalMonthly)}/mo</div>
          </div>
        </div>

        {/* Dubai-style package banner */}
        {profile.packageMarket && (
          <div className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-primary/5 border border-primary/20">
            <Info className="w-4 h-4 text-primary shrink-0 mt-0.5" />
            <p className="text-xs text-foreground">
              <span className="font-medium">Package roles:</span>{" "}
              <span className="text-muted-foreground">
                Packages typically include housing, flights &amp; health insurance on top of the listed salary.
              </span>
            </p>
          </div>
        )}

        {/* Room-driven housekeeping seeding banner */}
        {totalRooms > 0 && (
          <div className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-primary/5 border border-primary/20">
            <Info className="w-4 h-4 text-primary shrink-0 mt-0.5" />
            <p className="text-xs text-foreground">
              <span className="font-medium">
                Housekeeping sized for {Math.round(occupiedRoomNights)} occupied
                room-night{Math.round(occupiedRoomNights) === 1 ? "" : "s"}/day
              </span>{" "}
              <span className="text-muted-foreground">
                ({totalRooms} total rooms).
                Room Attendants scale at ~1 per 14 occupied rooms; supervisors,
                turndown &amp; laundry follow industry ratios. Adjust counts to taste.
              </span>
            </p>
          </div>
        )}

        {/* Outlet-driven seeding banner */}
        {outlets.length > 0 && (
          <div className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-primary/5 border border-primary/20">
            <Info className="w-4 h-4 text-primary shrink-0 mt-0.5" />
            <p className="text-xs text-foreground">
              <span className="font-medium">Seeded from your {outlets.length} outlet{outlets.length === 1 ? "" : "s"}:</span>{" "}
              <span className="text-muted-foreground">
                {outlets.map(o => `${o.name || o.type}${o.seats ? ` (${o.seats} seats)` : ""}`).join(" · ")}.
                Adjust counts below to fine-tune.
              </span>
            </p>
          </div>
        )}


        <Accordion type="multiple" className="w-full">
          {departments.map(dept => (
            <AccordionItem key={dept.id} value={dept.id} className="border-border">
              <AccordionTrigger className="hover:no-underline py-3">
                <div className="flex items-center justify-between w-full pr-3 gap-3">
                  <div className="flex flex-col items-start text-left">
                    <span className="text-sm font-medium text-foreground">{dept.label}</span>
                    <span className="text-[11px] text-muted-foreground line-clamp-1">
                      {dept.description}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-right shrink-0">
                    <span className="text-[11px] text-muted-foreground">
                      {departmentStaff[dept.id] || 0} staff
                    </span>
                    <span className="text-xs font-medium text-primary tabular-nums">
                      {fmtMoney(departmentTotals[dept.id] || 0)}/mo
                    </span>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-2 pt-1">
                  {dept.roles.map(role => {
                    const key = `${dept.id}::${role.name}`;
                    const count = counts[key] || 0;
                    return (
                      <div
                        key={key}
                        className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border/50"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-foreground">{role.name}</span>
                            <Badge
                              variant={priorityVariant(role.priority)}
                              className="text-[10px] px-1.5 py-0 capitalize"
                            >
                              {role.priority}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                            {role.description}
                          </p>
                          <div className="text-[11px] text-muted-foreground mt-0.5">
                            {role.rangeLabel}
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <Button
                            size="icon"
                            variant="outline"
                            className="h-7 w-7"
                            onClick={() => update(key, -1)}
                            disabled={count === 0}
                            aria-label={`Decrease ${role.name}`}
                          >
                            <Minus className="w-3 h-3" />
                          </Button>
                          <span className="w-6 text-center text-sm font-medium text-foreground tabular-nums">
                            {count}
                          </span>
                          <Button
                            size="icon"
                            variant="outline"
                            className="h-7 w-7"
                            onClick={() => update(key, 1)}
                            aria-label={`Increase ${role.name}`}
                          >
                            <Plus className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        <div className="mt-4 pt-3 border-t border-border flex justify-between items-center">
          <span className="text-sm font-medium text-foreground">Est. Total Monthly Labor</span>
          <span className="text-lg font-medium text-primary">{fmtMoney(totalMonthly)}/mo</span>
        </div>

        {/* Global luxury-tier footnote */}
        <div className="mt-3 flex items-start gap-1.5 text-[11px] text-muted-foreground">
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="w-3 h-3 mt-0.5 shrink-0 cursor-help" />
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs text-xs">
              Salaries reflect typical 2025/2026 luxury hospitality benchmarks. Junior tiers,
              mid-market venues, and casual concepts will pay less.
            </TooltipContent>
          </Tooltip>
          <span>
            Ranges reflect luxury / 5-star establishments. Actual compensation may vary based on
            experience and property tier.
          </span>
        </div>
      </motion.div>
    </TooltipProvider>
  );
}
